import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api-client.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { DataTable, type Column } from '../components/ui/data-table.js';
import { EmptyState } from '../components/ui/empty-state.js';
import { Input, Select } from '../components/ui/input.js';

type CampaignStatus = 'pending' | 'in_progress' | 'completed' | 'failed';
type CampaignChannel = 'auto_dialer' | 'ai_message' | 'sms' | 'email';

interface Campaign {
  id: string;
  tag: string;
  recipient_name?: string;
  recipient_phone: string;
  recipient_email?: string;
  start_at: string;
  channels: CampaignChannel[];
  status: CampaignStatus;
  created_at: string;
}

interface ListResponse {
  data: Campaign[];
  pagination: { next_cursor: string | null; has_more: boolean };
}

const STATUS_TONE: Record<CampaignStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  pending: 'neutral',
  in_progress: 'info',
  completed: 'success',
  failed: 'danger',
};

const CHANNEL_LABEL: Record<CampaignChannel, string> = {
  auto_dialer: 'dialer',
  ai_message: 'ai',
  sms: 'sms',
  email: 'email',
};

export function CampaignsPage(): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | CampaignStatus>('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get('/v1/campaigns') as Promise<ListResponse>,
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    return data.data.filter((row) => {
      if (statusFilter && row.status !== statusFilter) return false;
      if (!s) return true;
      return [row.tag, row.recipient_name, row.recipient_phone, row.recipient_email]
        .some((v) => v && v.toLowerCase().includes(s));
    });
  }, [data, search, statusFilter]);

  const hasFilter = !!search || !!statusFilter;

  const cancel = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/campaigns/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  const columns: Column<Campaign>[] = [
    {
      key: 'tag',
      header: 'Tag',
      render: (row) => (
        <Link to={`/campaigns/${row.id}`} className="font-medium hover:text-accent">
          {row.tag}
        </Link>
      ),
    },
    {
      key: 'recipient',
      header: 'Recipient',
      render: (row) => (
        <div className="text-sm">
          {row.recipient_name && <div>{row.recipient_name}</div>}
          <code className="text-xs text-ink-muted">{row.recipient_phone}</code>
        </div>
      ),
    },
    {
      key: 'when',
      header: 'When',
      render: (row) => (
        <div className="text-sm">
          <div>{new Date(row.start_at).toLocaleDateString()}</div>
          <div className="text-xs text-ink-muted">
            {new Date(row.start_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      ),
      width: '120px',
    },
    {
      key: 'channels',
      header: 'Channels',
      render: (row) => (
        <div className="flex gap-1">
          {row.channels.map((c) => (
            <Badge key={c} tone="neutral">
              {CHANNEL_LABEL[c]}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge tone={STATUS_TONE[row.status]}>{row.status.replace('_', ' ')}</Badge>,
      width: '120px',
    },
    {
      key: 'actions',
      header: '',
      width: '150px',
      className: 'text-right',
      render: (row) => (
        <div className="flex justify-end gap-1">
          {row.status === 'pending' ? (
            <Link to={`/campaigns/${row.id}/edit`}>
              <Button variant="ghost" size="sm">Edit</Button>
            </Link>
          ) : (
            <Button variant="ghost" size="sm" disabled title="Editable only while pending">
              Edit
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (window.confirm(`Cancel campaign "${row.tag}"?`)) cancel.mutate(row.id);
            }}
            disabled={cancel.isPending}
          >
            Cancel
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Campaigns</h1>
          <p className="text-sm text-ink-muted">
            Outbound messaging — auto-dialer, AI message, SMS, email. Fires once at start_at.
          </p>
        </div>
        <Link to="/campaigns/new">
          <Button>New campaign</Button>
        </Link>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>All campaigns</CardTitle>
          {data && (
            <span className="text-xs text-ink-muted">
              {hasFilter ? `${filtered.length} of ${data.data.length}` : data.data.length}
              {data.pagination.has_more && !hasFilter ? '+' : ''}
            </span>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {data && (
            <div className="flex gap-2 px-4 py-3 border-b border-border">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tag, recipient name, phone, email…"
                className="flex-1 min-w-0"
              />
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as '' | CampaignStatus)}
                className="w-44"
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In progress</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </Select>
              {hasFilter && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); setStatusFilter(''); }}
                  className="text-sm text-ink-muted hover:text-ink px-2"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          {isLoading && <p className="p-4 text-sm text-ink-muted">Loading…</p>}
          {error && <ErrorBanner error={error} />}
          {data && (
            <DataTable
              data={filtered}
              columns={columns}
              rowKey={(r) => r.id}
              emptyState={
                hasFilter ? (
                  <EmptyState
                    title="No matches"
                    description="No campaigns match the current filters. Try a different term or clear them."
                  />
                ) : (
                  <EmptyState
                    title="No campaigns yet"
                    description="Create one to fire a one-shot message via the connector + BYO email at start_at."
                  />
                )
              }
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="m-4 rounded border border-danger bg-surface p-3 text-sm">
      <p className="font-medium text-danger">Failed to load</p>
      <p className="mt-1 text-ink-muted">
        {status ? `HTTP ${status}` : 'Network error'} — {(error as Error).message}
      </p>
    </div>
  );
}
